import type { TimetableCourse, TimetableSession } from "../../lib/api";
import { CATEGORY_MAP, FALLBACK_CATEGORY, type CategoryStyle } from "./theme";

export type YearMonth = { year: number; month: number }; // month is 1-12
export type GridCell = { dayKey: string; day: number; inMonth: boolean };

export function titleCase(s: string): string {
  return s.replace(/\b\p{L}/gu, (c) => c.toUpperCase());
}

export function resolveCategory(
  course?: Pick<TimetableCourse, "tags" | "fixedTags">,
): CategoryStyle {
  const candidates = [...(course?.fixedTags ?? []), ...(course?.tags ?? [])];
  for (const tag of candidates) {
    const name = tag.name?.trim().toLowerCase();
    if (name && CATEGORY_MAP[name]) {
      return { key: name, label: titleCase(name), ...CATEGORY_MAP[name] };
    }
  }
  return FALLBACK_CATEGORY;
}

// Intl.DateTimeFormat construction is ~0.1-1ms; the timetable renders one per
// session per render (times, day keys, day headings), so cache instances
// keyed by locale + role + timezone rather than rebuilding them each call.
const formatterCache = new Map<string, Intl.DateTimeFormat>();

function getFormatter(
  locale: string,
  role: string,
  timeZone: string,
  options: Intl.DateTimeFormatOptions,
): Intl.DateTimeFormat {
  const key = `${locale}|${role}|${timeZone}`;
  let fmt = formatterCache.get(key);
  if (!fmt) {
    fmt = new Intl.DateTimeFormat(locale, { timeZone, ...options });
    formatterCache.set(key, fmt);
  }
  return fmt;
}

export function dayKeyInTz(iso: string, timeZone: string): string {
  // en-CA formats as YYYY-MM-DD
  return getFormatter("en-CA", "dayKey", timeZone, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date(iso));
}

export function timeInTz(iso: string, timeZone: string): string {
  return getFormatter("en-GB", "time", timeZone, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(new Date(iso));
}

export function dayHeadingInTz(iso: string, timeZone: string): string {
  return getFormatter("en-GB", "dayHeading", timeZone, {
    weekday: "long",
    day: "numeric",
    month: "long",
  }).format(new Date(iso));
}

export function monthTitle(ym: YearMonth): string {
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: "UTC",
    month: "long",
    year: "numeric",
  }).format(new Date(Date.UTC(ym.year, ym.month - 1, 1)));
}

export function monthKey(ym: YearMonth): string {
  return `${ym.year}-${String(ym.month).padStart(2, "0")}`;
}

export function addMonths(ym: YearMonth, delta: number): YearMonth {
  const zeroBased = ym.year * 12 + (ym.month - 1) + delta;
  return { year: Math.floor(zeroBased / 12), month: (zeroBased % 12 + 12) % 12 + 1 };
}

export function compareYearMonth(a: YearMonth, b: YearMonth): number {
  return a.year * 12 + a.month - (b.year * 12 + b.month);
}

function utcDayKey(d: Date): string {
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(d.getUTCDate()).padStart(2, "0")}`;
}

export function monthFetchRange(ym: YearMonth): { from: string; to: string } {
  // one day of slack each side so timezone offsets never clip the month
  const from = new Date(Date.UTC(ym.year, ym.month - 1, 1) - 24 * 3600 * 1000);
  const to = new Date(Date.UTC(ym.year, ym.month, 1)); // 1st of next month = last day + 1
  return { from: utcDayKey(from), to: utcDayKey(to) };
}

export function buildMonthGrid(ym: YearMonth): GridCell[][] {
  const first = new Date(Date.UTC(ym.year, ym.month - 1, 1));
  const mondayOffset = (first.getUTCDay() + 6) % 7; // 0 if the 1st is a Monday
  const start = new Date(first.getTime() - mondayOffset * 24 * 3600 * 1000);
  const lastDay = new Date(Date.UTC(ym.year, ym.month, 0)); // day 0 of next month
  const weeks: GridCell[][] = [];
  const cursor = new Date(start);
  while (cursor.getTime() <= lastDay.getTime() || weeks[weeks.length - 1]?.length !== 7) {
    if (!weeks.length || weeks[weeks.length - 1].length === 7) weeks.push([]);
    weeks[weeks.length - 1].push({
      dayKey: utcDayKey(cursor),
      day: cursor.getUTCDate(),
      inMonth: cursor.getUTCMonth() === ym.month - 1 && cursor.getUTCFullYear() === ym.year,
    });
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return weeks;
}

export function isInMonth(dayKey: string, ym: YearMonth): boolean {
  return dayKey.startsWith(`${monthKey(ym)}-`);
}

export function groupSessionsByDay(
  sessions: TimetableSession[],
  tz: string,
): Map<string, TimetableSession[]> {
  const sorted = [...sessions].sort((a, b) =>
    a.startDateTime.localeCompare(b.startDateTime),
  );
  const grouped = new Map<string, TimetableSession[]>();
  for (const s of sorted) {
    const key = dayKeyInTz(s.startDateTime, tz);
    const bucket = grouped.get(key);
    if (bucket) bucket.push(s);
    else grouped.set(key, [s]);
  }
  return grouped;
}
